import {
  SearchOutlined,
  DescriptionOutlined,
  VisibilityOutlined,
  FileDownloadOutlined,
  TrendingUp,
  ScheduleOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  EmailOutlined,
} from '@mui/icons-material';
import { CardContainer, DataTable, Tag, Button, Dropdown, SearchBar } from 'finara-components';
import type { DataTableColumn, DropdownOption } from 'finara-components';
import { useState } from 'react';

import { CsvExportButton } from '@/components/common';
import {
  generatedReports,
  employeeMovements,
  receipts,
  scheduledReports,
  monthlyMetrics,
  departmentActivity,
} from '@/data/admin/reports/reportsData';
import type { EmployeeMovement } from '@/data/admin/reports/reportsData';
import { formatNumber } from '@/utils/formatters';

interface Employee {
  id: string;
  name: string;
  department: string;
  employeeId: string;
}

interface IndividualReport {
  id: string;
  name: string;
  description: string;
}

const employees: Employee[] = [
  { id: '1', name: 'Carlos Mendoza López', department: 'Ventas', employeeId: 'EMP-002' },
  { id: '2', name: 'María Elena Rodríguez', department: 'Marketing', employeeId: 'EMP-003' },
  { id: '3', name: 'Roberto Vásquez Herrera', department: 'Finanzas', employeeId: 'EMP-004' },
  { id: '4', name: 'Patricia Gómez Torres', department: 'Recursos Humanos', employeeId: 'EMP-005' },
];

const individualReports: IndividualReport[] = [
  { id: '1', name: 'Historial Completo', description: 'Todas las operaciones del empleado' },
  { id: '2', name: 'Estado de Cuenta', description: 'Balance y límites actuales' },
  { id: '3', name: 'Comprobantes de Nómina', description: 'Recibos por período' },
  { id: '4', name: 'Historial de Adelantos', description: 'Adelantos solicitados y pagados' },
];

export const IndividualReportsTab: React.FC = () => {
  const [selectedReceipt, setSelectedReceipt] = useState<string | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);

  const departmentOptions: DropdownOption[] = [
    { value: '', label: 'Todos' },
    { value: 'Ventas', label: 'Ventas' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Finanzas', label: 'Finanzas' },
    { value: 'Recursos Humanos', label: 'Recursos Humanos' },
  ];

  const filteredEmployees = employees.filter(emp => {
    const matchesDepartment = departmentFilter ? emp.department === departmentFilter : true;
    const matchesSearch = searchTerm
      ? emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    return matchesDepartment && matchesSearch;
  });

  const toggleEmployee = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    );
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      nomina: 'nomina',
      adelanto: 'adelanto',
      reembolso: 'reembolso',
      bonus: 'bonus',
    };
    return labels[type] || type;
  };

  const getTypeVariant = (type: string) => {
    const variants: Record<string, 'blue' | 'red' | 'yellow' | 'green'> = {
      nomina: 'blue',
      adelanto: 'red',
      reembolso: 'yellow',
      bonus: 'yellow',
    };
    return variants[type] || 'blue';
  };

  const movementsColumns: DataTableColumn<EmployeeMovement>[] = [
    {
      key: 'date',
      label: 'Fecha',
      render: (_, movement) => (
        <div>
          <p className="text-sm text-gray-900">{movement.date}</p>
          <p className="text-xs text-gray-500">{movement.time}</p>
        </div>
      ),
    },
    {
      key: 'employee',
      label: 'Empleado',
      render: (_, movement) => (
        <div>
          <p className="text-sm text-gray-900">{movement.employee}</p>
          <p className="text-xs text-gray-500">{movement.employeeId}</p>
        </div>
      ),
    },
    {
      key: 'department',
      label: 'Departamento',
      render: (_, movement) => <span className="text-sm text-gray-900">{movement.department}</span>,
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, movement) => (
        <Tag variant={getTypeVariant(movement.type)}>{getTypeLabel(movement.type)}</Tag>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, movement) => (
        <span className="text-sm font-semibold text-gray-900">
          ${formatNumber(movement.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, movement) => <Tag variant="green">{movement.status}</Tag>,
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: (_, movement) => <span className="text-sm text-gray-900">{movement.reference}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Generation Section with Sub-tabs */}
      <CardContainer>
        <div>
          <div className="flex items-center gap-2 mb-6">
            <DescriptionOutlined className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Generación de Reportes</h3>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Left Side - Employee Selection & Individual Reports */}
            <div className="space-y-6">
              {/* Employee Selection */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <SearchOutlined className="text-gray-700" />
                  <h4 className="text-sm font-semibold text-gray-900">Seleccionar Empleados</h4>
                </div>

                <div className="grid grid-cols-1 md:!grid-cols-2 gap-4 mb-4">
                  <div className="w-full">
                    <label
                      htmlFor="department"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Departamento
                    </label>
                    <Dropdown
                      id="department"
                      className="w-full"
                      value={departmentFilter}
                      onChange={value => setDepartmentFilter(String(value))}
                      options={departmentOptions}
                      placeholder="Todos"
                      searchable
                    />
                  </div>
                  <div className="w-full">
                    <label
                      htmlFor="search-employee"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Buscar Empleado
                    </label>
                    <SearchBar
                      className="w-full h-9"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder="Nombre o ID empleado..."
                    />
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <div className="space-y-2">
                    {filteredEmployees.map(employee => (
                      <div
                        key={employee.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => toggleEmployee(employee.id)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{employee.name}</p>
                          <p className="text-xs text-gray-500">
                            {employee.department} - {employee.employeeId}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Individual Reports List */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <DescriptionOutlined className="text-gray-700" />
                  <h4 className="text-sm font-semibold text-gray-900">Reportes Individuales</h4>
                </div>

                <div className="space-y-3">
                  {individualReports.map(report => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-3">
                        <DescriptionOutlined className="text-gray-400 mt-1" fontSize="small" />
                        <div>
                          <p className="font-medium text-gray-900">{report.name}</p>
                          <p className="text-sm text-gray-500">{report.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Selecciona uno o más empleados para generar reportes individuales.
                </p>
              </div>
            </div>

            {/* Right Side - Generated Reports */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Reportes Generados</h4>
              <div className="space-y-3">
                {generatedReports.slice(2, 3).map(report => (
                  <div
                    key={report.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{report.title}</p>
                        <p className="text-sm text-gray-500">{report.employees}</p>
                        <p className="text-xs text-gray-500">{report.period}</p>
                      </div>
                      <Tag variant={report.status === 'Listo' ? 'green' : 'blue'}>
                        {report.status}
                      </Tag>
                    </div>
                    {report.status === 'Listo' && report.size && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{report.format}</span>
                          <span className="text-xs text-gray-500">{report.size}</span>
                        </div>
                        <Button variant="white" size="small" startIcon={<FileDownloadOutlined />}>
                          Descargar
                        </Button>
                      </div>
                    )}
                    {report.status === 'Procesando' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-xs text-gray-500">{report.format}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Employee Movements Table */}
      <CardContainer>
        <div>
          <div className="flex flex-col lg:!flex-row lg:!items-center lg:!justify-between mb-6">
            <div className="flex items-center gap-2 mb-4 lg:!mb-0">
              <TrendingUp className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Movimientos de Empleados</h3>
            </div>
            <div className="flex items-center gap-3">
              <CsvExportButton
                data={employeeMovements}
                filename="movimientos_empleados_individual_excel"
                columns={movement => ({
                  Fecha: movement.date,
                  Hora: movement.time,
                  Empleado: movement.employee,
                  'ID Empleado': movement.employeeId,
                  Departamento: movement.department,
                  Tipo: getTypeLabel(movement.type),
                  Monto: formatNumber(movement.amount),
                  Estado: movement.status,
                  Referencia: movement.reference,
                })}
                variant="white"
                size="small"
                buttonText="Exportar Excel"
              />
              <CsvExportButton
                data={employeeMovements}
                filename="movimientos_empleados_individual"
                columns={movement => ({
                  Fecha: movement.date,
                  Hora: movement.time,
                  Empleado: movement.employee,
                  'ID Empleado': movement.employeeId,
                  Departamento: movement.department,
                  Tipo: getTypeLabel(movement.type),
                  Monto: formatNumber(movement.amount),
                  Estado: movement.status,
                  Referencia: movement.reference,
                })}
                variant="white"
                size="small"
                buttonText="Exportar CSV"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <DataTable columns={movementsColumns} data={employeeMovements} />
          </div>
        </div>
      </CardContainer>

      {/* Receipts Section */}
      <CardContainer>
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <DescriptionOutlined className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Comprobantes Empresariales</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Comprobantes Disponibles */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Comprobantes Disponibles</h4>
              <div className="space-y-3">
                {receipts.map(receipt => (
                  <button
                    key={receipt.id}
                    className={`w-full text-left p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedReceipt === receipt.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedReceipt(receipt.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{receipt.reference}</p>
                        <p className="text-sm text-gray-500">{receipt.employee}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {receipt.tags.map((tag, index) => (
                            <Tag key={index} variant="white">
                              {tag}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button variant="white" size="small" startIcon={<VisibilityOutlined />}>
                        Ver
                      </Button>
                      <Button variant="white" size="small" startIcon={<FileDownloadOutlined />}>
                        PDF
                      </Button>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <Button
                  variant="white"
                  size="small"
                  startIcon={<FileDownloadOutlined />}
                  className="w-full"
                >
                  Descargar Lote Completo
                </Button>
              </div>
            </div>

            {/* Vista Previa PDF */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Vista Previa PDF</h4>
              <CardContainer>
                <div className="p-12 text-center">
                  <DescriptionOutlined
                    className="text-gray-400 mx-auto mb-4"
                    style={{ fontSize: '4rem' }}
                  />
                  <p className="text-gray-600">Selecciona un comprobante para visualizar</p>
                </div>
              </CardContainer>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Scheduled Reports Section */}
      <CardContainer>
        <div>
          <div className="flex items-center gap-2 mb-6">
            <ScheduleOutlined className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Reportes Programados</h3>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Left Side - Nueva Programación */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-900">Nueva Programación</h4>
                <Button variant="white" size="small" startIcon={<SendOutlined />}>
                  Crear Programación
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="report-type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Tipo de Reporte
                  </label>
                  <Dropdown
                    id="report-type"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'nomina', label: 'Resumen de Nómina' },
                      { value: 'adelantos', label: 'Reporte de Adelantos' },
                      { value: 'departamento', label: 'Operaciones por Departamento' },
                    ]}
                    placeholder="Seleccionar tipo de reporte"
                  />
                </div>

                <div>
                  <label
                    htmlFor="frequency"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Frecuencia
                  </label>
                  <Dropdown
                    id="frequency"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'semanal', label: 'Semanal' },
                      { value: 'mensual', label: 'Mensual' },
                    ]}
                    placeholder="Seleccionar frecuencia"
                  />
                </div>

                <div>
                  <label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-2">
                    Formato
                  </label>
                  <Dropdown
                    id="format"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'pdf', label: 'PDF' },
                      { value: 'excel', label: 'Excel' },
                    ]}
                    placeholder="Seleccionar formato"
                  />
                </div>

                <div>
                  <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-2">
                    Día de Envío
                  </label>
                  <Dropdown
                    id="day"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'lunes', label: 'Lunes' },
                      { value: 'martes', label: 'Martes' },
                      { value: 'miercoles', label: 'Miércoles' },
                      { value: 'jueves', label: 'Jueves' },
                      { value: 'viernes', label: 'Viernes' },
                    ]}
                    placeholder="Seleccionar día"
                  />
                </div>

                <div>
                  <label
                    htmlFor="recipients"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Destinatarios
                  </label>
                  <input
                    id="recipients"
                    type="text"
                    placeholder="admin@techcorp.com, finanzas@techcorp.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Right Side - Programaciones Activas */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Programaciones Activas</h4>
              <div className="space-y-3">
                {scheduledReports.map(report => (
                  <div
                    key={report.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{report.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Tag variant="blue">{report.frequency}</Tag>
                          <Tag variant="white">{report.format}</Tag>
                          <Tag variant="green">{report.status}</Tag>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      <p>Próximo envío: {report.nextSend}</p>
                      <p className="flex items-center gap-1">
                        <EmailOutlined fontSize="small" />
                        {report.recipients} destinatarios
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                      <Button variant="white" size="small" startIcon={<EditOutlined />}>
                        Editar
                      </Button>
                      <Button variant="white" size="small" startIcon={<SendOutlined />}>
                        Enviar Ahora
                      </Button>
                      <button className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded ml-auto">
                        <DeleteOutlined fontSize="small" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Análisis Empresarial Section */}
      <CardContainer>
        <div>
          <div className="flex items-center gap-2 mb-6">
            <BarChartOutlined className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Análisis Empresarial</h3>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Monthly Metrics */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Métricas del Mes</h4>
              <div className="space-y-3">
                {monthlyMetrics.map((metric, index) => (
                  <CardContainer key={index} className="space-y-4">
                    <span className="text-sm text-gray-700">{metric.label}</span>
                    <div className="text-xl font-semibold text-gray-900">{metric.value}</div>
                    {metric.trend && <div className="text-xs">{metric.trend}</div>}
                  </CardContainer>
                ))}
              </div>
            </div>

            {/* Department Activity */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-900">
                  Departamentos con Mayor Actividad
                </h4>
                <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  Ver Análisis Detallado
                  <VisibilityOutlined fontSize="small" />
                </button>
              </div>
              <div className="space-y-3">
                {departmentActivity.map((dept, index) => (
                  <CardContainer key={index}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{dept.name}</p>
                        <p className="text-sm text-gray-500">{dept.operations} operaciones</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${formatNumber(dept.volume)}</p>
                        <p className="text-xs text-gray-500">Volumen</p>
                      </div>
                    </div>
                  </CardContainer>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>
    </div>
  );
};
